import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TelegramLogo, ArrowsClockwise, PaperPlaneTilt } from "@phosphor-icons/react";

export function TelegramTab({ alerts, onRefresh }) {
  const [discovering, setDiscovering] = useState(false);
  const [chats, setChats] = useState(null);
  const [sending, setSending] = useState(false);
  const configured = !!alerts?.telegram_configured;

  const discover = async () => {
    setDiscovering(true);
    try {
      const { data } = await api.get("/admin/telegram/discover-chat");
      if (data.error) toast.error(data.error);
      else setChats(data.chats || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error consultando Telegram");
    } finally { setDiscovering(false); }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await api.post("/admin/telegram/test");
      toast.success("✅ Mensaje de prueba enviado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando prueba");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="telegram-tab">
      {/* Status */}
      <div className={`rounded-2xl border-2 p-5 flex items-start gap-4 ${configured ? 'border-[#B4F34A]/30 bg-[#B4F34A]/5' : 'border-white/10 bg-[#121212]'}`}>
        <TelegramLogo size={36} weight="fill" className={configured ? "text-[#26A5E4]" : "text-white/30"} />
        <div className="flex-1">
          <p className="font-display font-bold text-lg">
            {configured ? "Notificaciones Telegram activas" : "Notificaciones Telegram desactivadas"}
          </p>
          <p className="text-sm text-white/60">
            {configured
              ? "Recibirás un mensaje cada vez que un usuario suba un comprobante de pago."
              : "Sigue los pasos abajo para activar las alertas instantáneas."}
          </p>
          {configured && (
            <button
              onClick={sendTest}
              disabled={sending}
              className="mt-3 px-4 py-2 rounded-full bg-[#26A5E4] hover:bg-[#1e8ec5] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              data-testid="tg-send-test"
            >
              <PaperPlaneTilt size={14} weight="fill" /> {sending ? "Enviando…" : "Enviar mensaje de prueba"}
            </button>
          )}
        </div>
      </div>

      {/* Setup steps */}
      <div className="rounded-2xl bg-[#121212] border border-white/10 p-5">
        <p className="font-display font-bold mb-4">Cómo configurarlo (3 minutos)</p>
        <ol className="space-y-4 text-sm text-white/80">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#FFB800] text-black text-xs font-bold flex items-center justify-center shrink-0">1</span>
            <div>
              <p className="font-semibold text-white">Crea tu bot en @BotFather</p>
              <p className="text-white/60 text-xs mt-1">
                Abre Telegram, busca <span className="text-[#26A5E4]">@BotFather</span>, envíale <code className="text-[#FFB800]">/newbot</code> y elige nombre. Te dará un token tipo <code className="text-[#FFB800]">123456:ABC-DEF...</code>
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#FFB800] text-black text-xs font-bold flex items-center justify-center shrink-0">2</span>
            <div>
              <p className="font-semibold text-white">Agrega el token a <code className="text-[#FFB800]">/app/backend/.env</code></p>
              <pre className="mt-2 p-3 rounded-lg bg-black/50 text-xs text-[#B4F34A] font-mono overflow-x-auto">TELEGRAM_BOT_TOKEN=123456:ABC-DEF...</pre>
              <p className="text-white/60 text-xs mt-1">Luego reinicia el backend (o pídele al desarrollador).</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#FFB800] text-black text-xs font-bold flex items-center justify-center shrink-0">3</span>
            <div>
              <p className="font-semibold text-white">Envía <code className="text-[#FFB800]">/start</code> a tu bot desde Telegram</p>
              <p className="text-white/60 text-xs mt-1">Búscalo por el username que elegiste y mándale un mensaje.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#FFB800] text-black text-xs font-bold flex items-center justify-center shrink-0">4</span>
            <div className="flex-1">
              <p className="font-semibold text-white">Encuentra tu chat_id aquí</p>
              <button
                onClick={discover}
                disabled={discovering}
                className="mt-2 px-4 py-2 rounded-full bg-[#26A5E4] hover:bg-[#1e8ec5] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="tg-discover"
              >
                <ArrowsClockwise size={14} weight="bold" className={discovering ? "animate-spin" : ""} /> {discovering ? "Buscando…" : "Detectar mi chat_id"}
              </button>
              {chats !== null && (
                <div className="mt-3 space-y-2" data-testid="tg-chat-list">
                  {chats.length === 0 ? (
                    <p className="text-xs text-white/50">Aún no se detectan chats. Manda <code className="text-[#FFB800]">/start</code> a tu bot y vuelve a buscar.</p>
                  ) : chats.map(c => (
                    <div key={c.chat_id} className="rounded-xl bg-[#0a0a0a] border border-white/10 p-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-semibold">{c.first_name || c.username || "Chat"}</p>
                        <p className="text-xs text-white/40">{c.type}</p>
                      </div>
                      <button
                        onClick={() => { navigator.clipboard.writeText(String(c.chat_id)); toast.success("Copiado"); }}
                        className="px-3 py-1.5 rounded-full bg-[#FFB800] text-black font-mono text-xs font-bold"
                      >
                        {c.chat_id} ⧉
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#FFB800] text-black text-xs font-bold flex items-center justify-center shrink-0">5</span>
            <div>
              <p className="font-semibold text-white">Agrega el chat_id a <code className="text-[#FFB800]">.env</code> y reinicia</p>
              <pre className="mt-2 p-3 rounded-lg bg-black/50 text-xs text-[#B4F34A] font-mono overflow-x-auto">TELEGRAM_ADMIN_CHAT_ID=123456789</pre>
              <p className="text-white/60 text-xs mt-1">Después regresa aquí y manda un mensaje de prueba.</p>
            </div>
          </li>
        </ol>
        <button
          onClick={onRefresh}
          className="mt-5 w-full py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-sm flex items-center justify-center gap-2"
        >
          <ArrowsClockwise size={14} /> Verificar configuración
        </button>
      </div>
    </div>
  );
}
